<script setup>
// Ay izgarasi: 7 sutun (Pzt..Paz) x 6 satir = 42 hucre.
//
// NEDEN kendi cizdik: harici takvim kutuphanesi eklemiyoruz. Sunucuda
// internet kisitli olabilir ve paket cekilemezse derleme kilitlenir; 42
// hucrelik bir izgara ise birkac satirlik tarih matematigi.
//
// Bilesen kendi tarih hesabini kendisi yapar ve disariya yalnizca iki olay
// bildirir: "su gune tiklandi", "su kayda tiklandi". Veri cekme ve kayit
// paneli Takvim.vue'da kalir; boylece asil ekran sismiyor.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  yil: { type: Number, required: true },
  // 0-11: JavaScript Date ile AYNI sayim. Kendi sayimimizi kurup arada
  // cevirmek en sik yaptigimiz bir-ay-kayma hatasiydi.
  ay: { type: Number, required: true },
  kayitlar: { type: Array, default: () => [] },
  // Panelde acik olan gunun anahtari ('YYYY-MM-DD'); o hucre vurgulanir.
  secili: { type: String, default: '' },
});

const emit = defineEmits(['gun-secildi', 'kayit-secildi']);

const { t, locale } = useI18n();

// Bir hucrede en fazla bu kadar kayit gorunur, gerisi "+N daha" olur.
// Daha fazlasi satir yuksekligini bozup izgarayi okunmaz yapiyordu.
const HUCRE_SINIRI = 3;

// Bozuk bir bitis tarihi (ornegin 3000 yili) donguyu kilitlemesin.
const EN_FAZLA_GUN = 60;

// Sunucu renk ADI gonderiyor, sinif adi degil: arayuzun tasarim
// tokenlarini arka uca sizdirmiyoruz.
const RENK_SINIFLARI = {
  mavi: 'text-n-blue-11',
  mor: 'text-n-iris-11',
  yesil: 'text-n-teal-11',
  gri: 'text-n-slate-11',
};

// Chatwoot dil kodlari yer yer alt cizgili geliyor (pt_BR gibi); Intl bunu
// gecersiz sayip hata firlatir, tireye cevirmek gerekiyor.
const bicimDili = computed(() => String(locale.value || 'tr').replace('_', '-'));

const iki = sayi => String(sayi).padStart(2, '0');

// Gun anahtari YEREL tarihten uretilir. toISOString() kullanirsak saat
// dilimi yuzunden kayit bir onceki/sonraki gune dusebiliyor.
const gunAnahtari = tarih =>
  `${tarih.getFullYear()}-${iki(tarih.getMonth() + 1)}-${iki(tarih.getDate())}`;

const gecerliTarih = deger => {
  const tarih = new Date(deger);
  return Number.isNaN(tarih.getTime()) ? null : tarih;
};

// --- izgara ----------------------------------------------------------------

// Hafta Pazartesi baslar (Turkiye). getDay() 0'i Pazar sayiyor; Pazar'i
// haftanin sonuna tasimak icin +6 alip 7'ye mod aliyoruz.
const izgaraBaslangici = computed(() => {
  const ayinIlki = new Date(props.yil, props.ay, 1);
  const kaydir = (ayinIlki.getDay() + 6) % 7;
  return new Date(props.yil, props.ay, 1 - kaydir);
});

// Gun basliklarini elle yazmiyoruz: Intl zaten kullanicinin dilinde veriyor,
// yeni bir dil eklendiginde burayi guncellemek gerekmesin.
const gunBasliklari = computed(() => {
  // 1 Ocak 2024 bir Pazartesi; oradan 7 gun ilerleyerek Pzt..Paz uretiyoruz.
  return Array.from({ length: 7 }, (_, sira) => {
    const gun = new Date(2024, 0, 1 + sira);
    try {
      return gun.toLocaleDateString(bicimDili.value, { weekday: 'short' });
    } catch (e) {
      return gun.toLocaleDateString('tr-TR', { weekday: 'short' });
    }
  });
});

// Tum gun kayitlari once, sonra saate gore: hucredeki satirlar gunun
// akisini izlesin.
const siralayici = (a, b) => {
  if (Boolean(a.tum_gun) !== Boolean(b.tum_gun)) return a.tum_gun ? -1 : 1;
  return new Date(a.baslangic).getTime() - new Date(b.baslangic).getTime();
};

// Kayitlari gun anahtarina dagitir.
//
// Cok gunlu kayit her gununde gorunur; yoksa "3 gun surecek" bir kayit
// yalnizca ilk gunde kalir ve kullanici ortadaki gunlerde onu goremezdi.
const gunlereGore = computed(() => {
  const kova = new Map();

  props.kayitlar.forEach(kayit => {
    const bas = gecerliTarih(kayit.baslangic);
    if (!bas) return;

    let bit = kayit.bitis ? gecerliTarih(kayit.bitis) : null;
    if (!bit || bit.getTime() < bas.getTime()) bit = bas;

    // Bitis tam gece yarisiysa kayit ertesi gune TASMASIN: 00:00'da biten
    // bir kayit onceki gunun sonudur. Arka uc gunu disliyan bir bitis
    // yazarsa (21 Eylul 00:00 gibi) kayit 20 Eylul'de kalmali.
    const geceYarisi =
      bit.getHours() === 0 &&
      bit.getMinutes() === 0 &&
      bit.getSeconds() === 0 &&
      bit.getMilliseconds() === 0;
    if (geceYarisi && bit.getTime() > bas.getTime()) {
      bit = new Date(bit.getTime() - 1);
    }

    let gun = new Date(bas.getFullYear(), bas.getMonth(), bas.getDate());
    const son = new Date(bit.getFullYear(), bit.getMonth(), bit.getDate());
    let sayac = 0;
    while (gun.getTime() <= son.getTime() && sayac < EN_FAZLA_GUN) {
      const anahtar = gunAnahtari(gun);
      if (!kova.has(anahtar)) kova.set(anahtar, []);
      kova.get(anahtar).push(kayit);
      gun = new Date(gun.getFullYear(), gun.getMonth(), gun.getDate() + 1);
      sayac += 1;
    }
  });

  kova.forEach(liste => liste.sort(siralayici));
  return kova;
});

const hucreler = computed(() => {
  // "Bugun" burada hesaplaniyor: computed'in disinda tutsaydik sekme gece
  // yarisini gecince vurgu yanlis gunde kalirdi.
  const bugun = gunAnahtari(new Date());
  const bas = izgaraBaslangici.value;

  return Array.from({ length: 42 }, (_, sira) => {
    const gun = new Date(
      bas.getFullYear(),
      bas.getMonth(),
      bas.getDate() + sira
    );
    const anahtar = gunAnahtari(gun);
    return {
      anahtar,
      gunNo: gun.getDate(),
      ayDisi: gun.getMonth() !== props.ay,
      bugunMu: anahtar === bugun,
      kayitlar: gunlereGore.value.get(anahtar) || [],
    };
  });
});

// --- "+N daha" -------------------------------------------------------------

// Genisletilmis hucreler. Ayri bir ekrana gitmek yerine hucreyi yerinde
// aciyoruz: kullanici acik ayi ve baktigi yeri kaybetmesin.
const acikGunler = ref([]);

const acikMi = anahtar => acikGunler.value.includes(anahtar);

const gunuAc = anahtar => {
  acikGunler.value = [...acikGunler.value, anahtar];
};

const gunuKapat = anahtar => {
  acikGunler.value = acikGunler.value.filter(a => a !== anahtar);
};

// Ay degisince genisletilmis hucreleri sifirliyoruz: yeni ayda o gunlerin
// karsiligi yok, acik kalan anahtarlar bellekte birikirdi.
watch(
  () => [props.yil, props.ay],
  () => {
    acikGunler.value = [];
  }
);

const gorunenler = hucre =>
  acikMi(hucre.anahtar) ? hucre.kayitlar : hucre.kayitlar.slice(0, HUCRE_SINIRI);

const gizliSayisi = hucre => Math.max(0, hucre.kayitlar.length - HUCRE_SINIRI);

// --- kayit satiri ----------------------------------------------------------

// Kayit kimlikleri turler arasinda cakisabilir: 'gonderi' kayitlari ICERIK
// bolumundeki BASKA bir tablodan turuyor, kimlik sayaclari ayri. Anahtari
// turle birlestirmezsek Vue iki ayri kaydi ayni sanip birini cizmiyor.
const kayitAnahtari = kayit => `${kayit.tur}-${kayit.id}`;

const renkSinifi = kayit => RENK_SINIFLARI[kayit.renk] || RENK_SINIFLARI.gri;

// Bitmis/iptal kayitlar listede kalsin ama one cikmasin.
const durumSinifi = kayit =>
  kayit.durum === 'tamamlandi' || kayit.durum === 'iptal'
    ? 'line-through opacity-60'
    : '';

// Saat YALNIZCA kaydin basladigi gunde yazilir: cok gunlu bir kaydin ikinci
// gununde baslangic saatini gostermek yanlis bilgi olurdu.
const saatMetni = (kayit, anahtar) => {
  if (kayit.tum_gun) return '';
  const bas = gecerliTarih(kayit.baslangic);
  if (!bas || gunAnahtari(bas) !== anahtar) return '';
  try {
    return bas.toLocaleTimeString(bicimDili.value, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return bas.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
};

// Baslik bos gelirse hucrede bos bir satir gorunurdu; tiklanabilir oldugu
// anlasilsin diye yer tutucu yaziyoruz.
const basligiGoster = kayit => kayit.baslik || t('TAKVIM.BASLIKSIZ');
</script>

<template>
  <!-- 7 sutun dar ekranda sikisiyor; izgarayi daraltmak yerine yatay
       kaydirma veriyoruz, hucre icerigi okunabilir kalsin. -->
  <div class="overflow-x-auto">
    <div class="min-w-[44rem]">
      <div
        class="grid grid-cols-7 border rounded-t-lg border-n-weak bg-n-alpha-1"
      >
        <div
          v-for="(ad, sira) in gunBasliklari"
          :key="sira"
          class="px-2 py-2 text-xs font-medium text-center capitalize text-n-slate-11"
        >
          {{ ad }}
        </div>
      </div>

      <!-- gap-px + zemin rengi: her hucreye ayri kenarlik vermeden temiz
           1 piksellik ayirici cikiyor. -->
      <div
        class="grid grid-cols-7 gap-px overflow-hidden border border-t-0 rounded-b-lg border-n-weak bg-n-weak"
      >
        <div
          v-for="hucre in hucreler"
          :key="hucre.anahtar"
          class="relative min-h-[7rem] p-1.5"
          :class="[
            hucre.ayDisi ? 'bg-n-alpha-1' : 'bg-n-surface-1',
            hucre.anahtar === secili ? 'ring-1 ring-inset ring-n-brand' : '',
          ]"
        >
          <!-- Bos alana tiklayinca o tarihle yeni kayit acilir. Ayri bir
               dugme olarak duruyor: kayit satirlari da dugme ve ic ice
               dugme gecersiz HTML. Ustteki icerik pointer-events-none,
               tiklanabilir parcalar tek tek geri aciliyor. -->
          <button
            type="button"
            class="absolute inset-0 w-full h-full rounded hover:bg-n-alpha-1"
            :aria-label="t('TAKVIM.GUN_EKLE')"
            @click="emit('gun-secildi', hucre.anahtar)"
          />

          <div class="relative flex flex-col gap-1 pointer-events-none">
            <span
              class="grid text-xs size-6 place-content-center"
              :class="[
                hucre.bugunMu
                  ? 'rounded-full bg-n-brand text-white font-medium'
                  : '',
                !hucre.bugunMu && hucre.ayDisi ? 'text-n-slate-10' : '',
                !hucre.bugunMu && !hucre.ayDisi ? 'text-n-slate-11' : '',
              ]"
            >
              {{ hucre.gunNo }}
            </span>

            <button
              v-for="kayit in gorunenler(hucre)"
              :key="kayitAnahtari(kayit)"
              type="button"
              class="flex items-center w-full gap-1 px-1 py-0.5 text-xs text-left rounded pointer-events-auto bg-n-alpha-1 hover:bg-n-alpha-2"
              :class="[renkSinifi(kayit), durumSinifi(kayit)]"
              :title="basligiGoster(kayit)"
              @click="emit('kayit-secildi', kayit)"
            >
              <span class="rounded-full shrink-0 size-1.5 bg-current" />
              <span v-if="saatMetni(kayit, hucre.anahtar)" class="shrink-0">
                {{ saatMetni(kayit, hucre.anahtar) }}
              </span>
              <span v-else-if="kayit.tum_gun" class="shrink-0">
                {{ t('TAKVIM.TUM_GUN_KISA') }}
              </span>
              <span class="truncate">{{ basligiGoster(kayit) }}</span>
              <!-- Icerik rozeti: bu kayit takvimden degil, ICERIK
                   bolumundeki zamanlanmis gonderiden geliyor. -->
              <span
                v-if="kayit.tur === 'gonderi'"
                class="ml-auto shrink-0 i-lucide-image size-3"
                :title="t('TAKVIM.ROZET_ICERIK')"
              />
            </button>

            <button
              v-if="gizliSayisi(hucre) && !acikMi(hucre.anahtar)"
              type="button"
              class="px-1 text-xs text-left pointer-events-auto text-n-slate-11 hover:text-n-slate-12"
              @click="gunuAc(hucre.anahtar)"
            >
              {{ t('TAKVIM.DAHA', { sayi: gizliSayisi(hucre) }) }}
            </button>
            <button
              v-else-if="acikMi(hucre.anahtar)"
              type="button"
              class="px-1 text-xs text-left pointer-events-auto text-n-slate-11 hover:text-n-slate-12"
              @click="gunuKapat(hucre.anahtar)"
            >
              {{ t('TAKVIM.DAHA_AZ') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
